import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Picture, PictureProps } from "@/components/wpl/image/src";

import { Button, ButtonProps, ButtonSize } from "../../button/src/button";
import { Link, LinkProps } from "../../link/src/link";
export interface ButtonAndLinkProps {
    button: ButtonProps;
    buttonText: string;
    link?: LinkProps;
}

export interface OneLinkProps {
    link: LinkProps;
}

export type ContentBlockCtaProps = ButtonAndLinkProps | OneLinkProps;

export type ContentBlockTitleAs = "h2" | "h3" | "h4";
export type ContentBlockAlignment = "start" | "center";

export type IconProps = {
    linkedIcon?: {
        url: string;
        size?: keyof typeof iconSizes;
    };
    iconComponent?: JSX.Element;
};

export interface ContentBlockProps {
    eyebrow?: string;
    title?: string;
    text?: string | React.ReactNode;
    titleAs?: ContentBlockTitleAs;
    pictureProps?: PictureProps;
    icon?: IconProps;
    callToActionProps?: ContentBlockCtaProps;
    alignment?: ContentBlockAlignment;
}

interface RenderCtaProps {
    ctaProps: ContentBlockCtaProps;
    buttonSize: ButtonSize;
    alignment: ContentBlockAlignment;
}

const imageStyle = css({
    // Issue in Picture component applies styles to picture instead of img
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        aspectRatio: "16 / 9",
        width: "100%",
        height: "auto",
        objectFit: "contain"
    }
});

const zeroMarginCss = css({ margin: 0 });

const hyphenStyles = css({
    hyphens: "auto"
});

const iconSizes = {
    "x-small": 24,
    "small": 32,
    "medium": 48,
    "large": 64
};

const renderCta = ({ ctaProps, buttonSize, alignment }: RenderCtaProps) => {
    if ("button" in ctaProps && ctaProps.button !== undefined) {
        return (
            <div
                css={css({
                    margin: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: token("space.250"),
                    alignItems: "center",
                    justifyContent: alignment
                })}
            >
                <Button
                    {...ctaProps.button}
                    size={buttonSize}
                    css={css({ flexBasis: "max-content" })}
                >
                    {ctaProps.buttonText}
                </Button>
                {ctaProps.link && <Link type="primary" {...ctaProps.link} />}
            </div>
        );
    }

    return (
        <div css={zeroMarginCss}>
            <Link type="primary" {...ctaProps.link} />
        </div>
    );
};

const iconContainer = css({
    // Design spec for icon usage is in progress, this is a temporary spacing solution
    marginBlockEnd: token("space.200")
});
const renderIcon = (icon: IconProps | undefined) => {
    if (icon?.linkedIcon) {
        const linkedIconSize = icon?.linkedIcon?.size
            ? iconSizes[icon.linkedIcon.size]
            : iconSizes["medium"];

        return (
            <div css={iconContainer}>
                <Picture
                    image={{
                        src: icon.linkedIcon.url,
                        alt: "",
                        width: linkedIconSize,
                        height: linkedIconSize
                    }}
                />
            </div>
        );
    }
    if (icon?.iconComponent) {
        return <div css={iconContainer}>{icon.iconComponent}</div>;
    }
    return undefined;
};

export const ContentBlock = ({
    eyebrow,
    title,
    text,
    pictureProps,
    icon,
    callToActionProps,
    alignment = "start",
    ...props
}: ContentBlockProps) => {
    const { titleAs: TitleComponent = "h3" } = props;

    const isSmallDetailStyling = ["h4"].includes(TitleComponent as string);
    const isLargeTextStyling = (TitleComponent as string) === "h2";
    const buttonSize: ButtonSize = isSmallDetailStyling ? "small" : "large";

    return (
        <div
            css={css({
                textAlign: alignment,
                display: "flex",
                flexDirection: "column",
                gap: token("space.300")
            })}
        >
            {pictureProps && (
                <div>
                    <Picture {...pictureProps} css={imageStyle} />
                </div>
            )}
            <div
                css={css({
                    display: "flex",
                    flexDirection: "column",
                    gap: token("space.100")
                })}
            >
                {eyebrow && (
                    <p
                        className={
                            isSmallDetailStyling ? "eyebrow-sm" : "eyebrow-md"
                        }
                        css={zeroMarginCss}
                    >
                        {eyebrow}
                    </p>
                )}
                {renderIcon(icon)}
                {title && (
                    <TitleComponent css={[zeroMarginCss, hyphenStyles]}>
                        {title}
                    </TitleComponent>
                )}
                {text && (
                    <p
                        className={isLargeTextStyling ? "body-lg" : "body-md"}
                        css={zeroMarginCss}
                    >
                        {text}
                    </p>
                )}
            </div>
            {callToActionProps &&
                renderCta({
                    ctaProps: callToActionProps,
                    buttonSize,
                    alignment
                })}
        </div>
    );
};
